import React from "react";
import VariantPanel, { type VariantFact } from "@/components/common/VariantPanel";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
interface PanelCopy {
  eyebrow: string;
  title: string;
  lead: string;
  points: string[];
  facts: VariantFact[];
  imageAlt: string;
}
const copyData: Record<Lang, PanelCopy> = {
  en: {
    eyebrow: "Powder & Particle Screening",
    title: "Precision Wire Mesh for Powder & Particle Screening",
    lead: "Engineered stainless steel woven wire mesh and fabricated screens designed for critical particle classification, high-frequency vibratory sieves, and ultrasonic separators. Providing aperture ratings from 20 µm to 25 mm (1 to 635 mesh) with anti-blinding geometry and custom frame assemblies.",
    points: ["Square weave aperture accuracy calibrated from 20 µm to 25 mm (1 to 635 mesh).", "High yield tension endurance under ultrasonic vibratory screening and high-frequency motion.", "Anti-blinding geometry minimizes near-mesh particle wedging and sustains throughput."],
    facts: [{
      label: "Mesh Range",
      value: "1 – 635 Mesh"
    }, {
      label: "Aperture Range",
      value: "20 µm – 25 mm"
    }, {
      label: "Alloy Grades",
      value: "AISI 304 / 316L"
    }, {
      label: "Assembly",
      value: "Ultrasonic / Tensioned"
    }],
    imageAlt: "Stainless steel wire mesh screen for powder and particle classification"
  },
  zh: {
    eyebrow: "粉体与颗粒筛分",
    title: "精密粉体与颗粒分级筛网",
    lead: "专为工业粉体分级、高频振动筛及超声波筛框量身定制的高精度不锈钢编织丝网与筛分组件。孔径覆盖 20 µm 至 25 mm（1 至 635 目），具备卓越的孔径均一性与抗嵌塞耐磨损性能。",
    points: ["方孔编织开孔公差精准校准，孔径覆盖 20 µm 至 25 mm（1 至 635 目）。", "适应超声波微振与高频振动筛分工况的高张力耐疲劳编织网面。", "防嵌塞几何构型减少临界粒径颗粒卡孔堵网，保持稳定透筛通量。"],
    facts: [{
      label: "目数规格",
      value: "1 – 635 目"
    }, {
      label: "筛分孔径",
      value: "20 µm – 25 mm"
    }, {
      label: "标准材质",
      value: "AISI 304 / 316L"
    }, {
      label: "装配形式",
      value: "超声波 / 预张紧筛环"
    }],
    imageAlt: "精密粉体与颗粒分级不锈钢编织丝网结构细节"
  },
  es: {
    eyebrow: "Tamizado de polvos y partículas",
    title: "Mallas de precisión para tamizado de polvos y partículas",
    lead: "Telas metálicas de acero inoxidable y cribas fabricadas para clasificación de partículas finas, tamices vibratorios de alta frecuencia y separadores ultrasónicos. Aberturas de 20 µm a 25 mm (1 a 635 mesh) con geometría anticolmatante y marcos a medida.",
    points: ["Precisión de abertura de malla cuadrada calibrada de 20 µm a 25 mm (1 a 635 mesh).", "Alta resistencia a la tracción bajo tamizado vibratorio ultrasónico y alta frecuencia.", "Geometría anticolmatante que reduce el atascamiento de partículas cercanas a la malla."],
    facts: [{
      label: "Rango de malla",
      value: "1 – 635 Mesh"
    }, {
      label: "Abertura",
      value: "20 µm – 25 mm"
    }, {
      label: "Aleaciones",
      value: "AISI 304 / 316L"
    }, {
      label: "Montaje",
      value: "Ultrasónico / Pretensado"
    }],
    imageAlt: "Malla de alambre de acero inoxidable para clasificación de partículas y polvos"
  },
  de: {
    eyebrow: "Pulver- & Partikelsiebung",
    title: "Präzisions-Drahtgewebe für die Pulver- & Partikelsiebung",
    lead: "Edelstahl-Drahtgewebe und Siebelemente für anspruchsvolle Partikelklassierung, Hochfrequenz-Vibrationssiebmaschinen und Ultraschall-Siebe. Maschenweiten von 20 µm bis 25 mm (1 bis 635 Mesh) mit verstopfungsarmer Geometrie und individueller Rahmenkonfektion.",
    points: ["Präzise Quadratmaschen-Öffnungen von 20 µm bis 25 mm (1 bis 635 Mesh).", "Hohe Dauerfestigkeit unter Ultraschall- und Hochfrequenz-Schwingbelastung.", "Verstopfungsarme Geometrie minimiert Partikelsteckkorn und erhält den Durchsatz."],
    facts: [{
      label: "Maschenbereich",
      value: "1 – 635 Mesh"
    }, {
      label: "Maschenweite",
      value: "20 µm – 25 mm"
    }, {
      label: "Werkstoffe",
      value: "AISI 304 / 316L"
    }, {
      label: "Konfektion",
      value: "Ultraschall / Vorgespannt"
    }],
    imageAlt: "Edelstahl-Drahtgewebe für die Pulver- und Partikelklassierung"
  },
  fr: {
    eyebrow: "Tamisage de poudres et particules",
    title: "Toiles métalliques de précision pour le tamisage de poudres et particules",
    lead: "Toiles métalliques tissées en acier inoxydable et éléments filtrants conçus pour la classification précise de particules, les tamiseurs vibrants haute fréquence et les séparateurs à ultrasons. Ouvertures de 20 µm à 25 mm (1 à 635 mesh) avec géométrie anticolmatage.",
    points: ["Précision d'ouverture calibrée en mailles carrées de 20 µm à 25 mm (1 à 635 mesh).", "Haute tenue sous vibration ultrasonique et sollicitations dynamiques rapides.", "Géométrie anticolmatage prévenant le coincement des grains de coupure critique."],
    facts: [{
      label: "Gamme de mailles",
      value: "1 – 635 mesh"
    }, {
      label: "Ouverture",
      value: "20 µm – 25 mm"
    }, {
      label: "Nuances d'alliage",
      value: "AISI 304 / 316L"
    }, {
      label: "Montage",
      value: "Ultrasons / Pré-tendu"
    }],
    imageAlt: "Toile métallique inox pour la séparation et le tamisage de poudres"
  },
  pt: {
    eyebrow: "Peneiramento de pós e partículas",
    title: "Telas de precisão para peneiramento de pós e partículas",
    lead: "Telas tecidas em aço inoxidável e elementos de classificação desenvolvidos para peneiramento crítico de partículas, peneiras vibratórias de alta frequência e separadores ultrassônicos. Aberturas de 20 µm a 25 mm (1 a 635 mesh) com geometria anti-entupimento.",
    points: ["Abertura em malha quadrada calibrada com precisão de 20 µm a 25 mm (1 a 635 mesh).", "Alta resistência à tração sob peneiramento vibratório ultrassônico de alta frequência.", "Geometria anti-entupimento que minimiza o travamento de partículas críticas."],
    facts: [{
      label: "Faixa de malha",
      value: "1 – 635 Mesh"
    }, {
      label: "Abertura",
      value: "20 µm – 25 mm"
    }, {
      label: "Ligas de aço",
      value: "AISI 304 / 316L"
    }, {
      label: "Montagem",
      value: "Ultrassônica / Pré-tensionada"
    }],
    imageAlt: "Tela de arame de aço inoxidável para classificação de pós e partículas"
  }
};
export default function PowderScreeningPanel() {
  const {
    lang
  } = useLanguage();
  const copy = pick(lang, copyData);
  return <VariantPanel id="powder-screening" eyebrow={copy.eyebrow} title={copy.title} lead={copy.lead} points={copy.points} facts={copy.facts} href="/PowderAndParticleScreeningPage" image={{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-83c744.png",
    alt: copy.imageAlt
  }} tone="background" />;
}